import {
  JSONSchema7,
  JSONSchema7Definition,
  JSONSchema7TypeName,
} from 'json-schema';

/**
 * Gets the primary type from a schema, handles both string and array types
 * @param schema - The JSON Schema to extract the primary type from
 * @returns The primary type name, or undefined if no type is specified
 */
export const getPrimaryType = (
  schema: JSONSchema7
): JSONSchema7TypeName | undefined => {
  if (!schema.type) return undefined;

  if (typeof schema.type === 'string') {
    return schema.type as JSONSchema7TypeName;
  }

  if (Array.isArray(schema.type) && schema.type.length > 0) {
    const nonNullTypes = schema.type.filter((t) => t !== 'null');
    if (nonNullTypes.length > 0) {
      const primaryType = nonNullTypes[0] as JSONSchema7TypeName;
      return primaryType;
    }
  }

  return undefined;
};

/**
 * Check if a schema is nullable (either has type: null or type: [..., null])
 * @param schema - The JSON Schema to check
 * @returns True if the schema allows null values
 */
export const isNullable = (schema: JSONSchema7): boolean => {
  if (!schema.type) return false;

  if (typeof schema.type === 'string') {
    return schema.type === 'null';
  }

  if (Array.isArray(schema.type)) {
    return schema.type.includes('null');
  }

  return false;
};

/**
 * Get a display name for a schema
 * @param schema - The JSON Schema to get the display name from
 * @param index - The index of the schema (used as fallback for display)
 * @returns A user-friendly display name for the schema
 */
export const getSchemaDisplayName = (
  schema: JSONSchema7,
  index: number
): string => {
  if (schema.title) {
    return schema.title;
  }

  if (schema.type === 'null') {
    // TODO: check when implementing i18n
    return 'Sin valor (null)';
  }

  const hasConstValue =
    schema.type === 'object' &&
    schema.properties &&
    schema.properties.type &&
    (schema.properties.type as JSONSchema7).const;
  if (hasConstValue) {
    const constValue = (schema.properties!.type as JSONSchema7).const;
    return constValue as string;
  }

  if (schema.type) {
    let typeName =
      typeof schema.type === 'string' ? schema.type : schema.type.join(' | ');

    if (schema.format) {
      typeName += ` (${schema.format})`;
    }
    return typeName;
  }

  // TODO: check when implementing i18n
  return `Opción ${index + 1}`;
};

/**
 * Helper function to create empty values based on schema type
 * @param schema - The JSON Schema definition to create an empty value for
 * @returns An empty/default value appropriate for the schema type
 */
export const getEmptyValueForSchema = (
  schema: JSONSchema7Definition
): unknown => {
  // Handle boolean schema (true/false are valid JSONSchema7Definition values)
  if (typeof schema === 'boolean') {
    return null;
  }

  const fieldType = getPrimaryType(schema);

  switch (fieldType) {
    case 'string': {
      const hasConstValue = schema.const !== undefined;
      if (hasConstValue) return schema.const;

      return '';
    }
    case 'number':
    case 'integer':
      return null;
    case 'boolean':
      return false;
    case 'object': {
      if (!schema.properties) {
        return {};
      }

      const result: Record<string, unknown> = {};
      Object.keys(schema.properties).forEach((key) => {
        const propSchema = schema.properties?.[key] as JSONSchema7;
        if (propSchema) {
          result[key] = getEmptyValueForSchema(propSchema);
        }
      });
      return result;
    }
    case 'array':
      return [];
    default:
      return null;
  }
};

/**
 * Type guard to check if a value is a record object
 * @param v - The value to check
 * @returns True if the value is a non-null object record
 */
export const isObjectRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

/**
 * Type guard to check if a value is a valid JSONSchema7 object
 * @param v - The value to check
 * @returns True if the value is a JSONSchema7 object
 */
export const isJsonSchema = (v: unknown): v is JSONSchema7 => isObjectRecord(v);

/**
 * Type guard to check if a schema has a properties field
 * @param s - The schema to check
 * @returns True if the schema is a JSONSchema7 with properties
 */
export const jsonSchemaHasProperties = (
  s: JSONSchema7
): s is JSONSchema7 & { properties: Record<string, JSONSchema7Definition> } =>
  isObjectRecord((s as JSONSchema7).properties);

/**
 * Extract the const type value from a schema's type property
 * @param s - The schema to extract the type from
 * @returns The const type string if found, otherwise undefined
 */
export const getConstTypeFromSchema = (s: JSONSchema7): string | undefined => {
  if (!jsonSchemaHasProperties(s)) return undefined;
  const typeDef = s.properties['type'];
  if (!isJsonSchema(typeDef)) return undefined;
  const constValue = typeDef.const;
  return typeof constValue === 'string' ? constValue : undefined;
};

/**
 * Detect which anyOf schema is selected based on current value or explicit index
 * @param anyOf - Array of anyOf schema definitions
 * @param currentValue - The current value to match against
 * @param explicitIndex - Optional explicit index to use
 * @returns The index of the selected schema
 */
export const detectAnyOfSelectedIndex = (
  anyOf: JSONSchema7Definition[] | undefined,
  currentValue: unknown,
  explicitIndex?: number
): number => {
  if (!Array.isArray(anyOf) || anyOf.length === 0) return 0;

  if (currentValue === null) {
    const idx = anyOf.findIndex(
      (entry) => isJsonSchema(entry) && entry.type === 'null'
    );
    if (idx >= 0) return idx;
  }

  const currentType = isObjectRecord(currentValue)
    ? (currentValue['type'] as unknown)
    : undefined;
  if (typeof currentType === 'string') {
    const idx = anyOf.findIndex((entry) => {
      if (!isJsonSchema(entry)) return false;
      return getConstTypeFromSchema(entry) === currentType;
    });
    if (idx >= 0) return idx;
  }

  const primitiveType = typeof currentValue;
  if (
    primitiveType === 'string' ||
    primitiveType === 'number' ||
    primitiveType === 'boolean'
  ) {
    const idx = anyOf.findIndex((entry) => {
      if (!isJsonSchema(entry)) return false;
      const entryType = entry.type;
      if (!entryType) return false;
      if (Array.isArray(entryType)) {
        if (primitiveType === 'number') {
          return entryType.includes('number') || entryType.includes('integer');
        }
        return entryType.includes(primitiveType);
      }
      if (primitiveType === 'number') {
        return entryType === 'number' || entryType === 'integer';
      }
      return entryType === primitiveType;
    });
    if (idx >= 0) return idx;
  }

  if (explicitIndex !== undefined) return explicitIndex;

  return 0;
};

/**
 * Build a merged value for anyOf selection, combining schema defaults with current values
 * @param selected - The selected anyOf schema
 * @param currentValue - The current value to merge with schema defaults
 * @returns A merged record combining schema defaults and current values
 */
export const buildMergedAnyOfValue = (
  selected: JSONSchema7 | undefined,
  currentValue: unknown
): unknown => {
  if (!selected) {
    return isObjectRecord(currentValue) ? currentValue : {};
  }

  const primaryType = getPrimaryType(selected);

  if (primaryType === 'null') return null;
  if (primaryType === 'string') {
    return typeof currentValue === 'string' ? currentValue : '';
  }
  if (primaryType === 'number' || primaryType === 'integer') {
    return typeof currentValue === 'number' ? currentValue : null;
  }
  if (primaryType === 'boolean') {
    return typeof currentValue === 'boolean' ? currentValue : false;
  }

  const prefillRaw = selected ? getEmptyValueForSchema(selected) : {};
  const prefill = isObjectRecord(prefillRaw) ? prefillRaw : {};
  const current = isObjectRecord(currentValue) ? currentValue : {};

  // Overlay current values onto schema defaults to preserve user input
  let merged: Record<string, unknown> = { ...prefill, ...current };

  // Enforce const type from selected schema if present
  if (selected) {
    const constType = getConstTypeFromSchema(selected);
    if (constType !== undefined) {
      merged = { ...merged, type: constType };
    }
  }

  return merged;
};

/**
 * Convert a value to a JSONSchema7, or return an empty schema if not valid
 * @param v - The value to convert
 * @returns A JSONSchema7 object
 */
export const toJsonSchema = (v: unknown): JSONSchema7 =>
  isJsonSchema(v) ? v : {};

/**
 * Check if a value is considered "empty" for cleaning purposes
 * @param v - The value to check
 * @returns True if the value should be removed during cleaning
 */
const isPrimitiveEmpty = (v: unknown): boolean => v === null || v === '';

/**
 * Clean an array by removing empty items recursively
 * @param arr - The array to clean
 * @param itemSchema - Optional schema for array items
 * @returns Cleaned array or undefined if empty
 */
const cleanArray = (
  arr: unknown[],
  itemSchema: JSONSchema7 | undefined
): unknown => {
  const cleanedItems = arr
    .map((item) => cleanValueForSchema(itemSchema, item))
    .filter((item) => item !== undefined);
  return cleanedItems.length === 0 ? undefined : cleanedItems;
};

/**
 * Clean an object by removing empty properties recursively
 * @param obj - The object to clean
 * @param schema - Optional schema with property definitions
 * @returns Cleaned object or undefined if empty
 */
const cleanObject = (
  obj: Record<string, unknown>,
  schema: JSONSchema7 | undefined
): unknown => {
  const cleanedObj: Record<string, unknown> = {};
  const props = schema?.properties || {};
  const fallbackSchema = schema?.additionalProperties as
    | JSONSchema7
    | undefined;

  Object.entries(obj).forEach(([key, val]) => {
    const propSchema = props?.[key] as JSONSchema7 | undefined;
    const schemaToUse = propSchema || fallbackSchema;
    const cleanedVal = cleanValueForSchema(schemaToUse, val);

    if (cleanedVal !== undefined) {
      cleanedObj[key] = cleanedVal;
    }
  });

  return Object.keys(cleanedObj).length === 0 ? undefined : cleanedObj;
};

/**
 * Clean a string value according to schema
 * @param v - The value to clean
 * @returns Cleaned value or undefined if empty
 */
const cleanString = (v: unknown): unknown =>
  isPrimitiveEmpty(v) ? undefined : v;

/**
 * Clean a numeric value according to schema
 * @param v - The value to clean
 * @returns Cleaned value or undefined if empty/NaN
 */
const cleanNumber = (v: unknown): unknown => {
  if (v === null) return undefined;
  if (typeof v === 'number' && Number.isNaN(v)) return undefined;
  return v;
};

/**
 * Clean a boolean value according to schema
 * @param v - The value to clean
 * @returns Cleaned value or undefined if null
 */
const cleanBoolean = (v: unknown): unknown => (v === null ? undefined : v);

/**
 * Clean a value without schema context by recursively cleaning collections
 * @param v - The value to clean
 * @returns Cleaned value or undefined if empty
 */
const cleanUnschemaedValue = (v: unknown): unknown => {
  if (isPrimitiveEmpty(v)) return undefined;

  if (Array.isArray(v)) {
    return cleanArray(v, undefined);
  }

  if (isObjectRecord(v)) {
    return cleanObject(v, undefined);
  }

  return v;
};

/**
 * Clean a value according to a JSON Schema definition.
 * - Returns undefined for empty values (so callers can omit keys).
 * - Recursively cleans objects/arrays.
 */
export const cleanValueForSchema = (
  schema: JSONSchema7 | undefined,
  value: unknown
): unknown => {
  if (value === undefined) return undefined;

  // If no schema provided, clean based on value type alone
  if (!schema) {
    return cleanUnschemaedValue(value);
  }

  const primaryType = getPrimaryType(schema);

  switch (primaryType) {
    case 'string':
      return cleanString(value);

    case 'number':
    case 'integer':
      return cleanNumber(value);

    case 'boolean':
      return cleanBoolean(value);

    case 'array': {
      if (!Array.isArray(value)) return value;
      const itemSchema = schema.items as JSONSchema7 | undefined;
      return cleanArray(value, itemSchema);
    }

    case 'object': {
      if (!isObjectRecord(value)) return value;
      return cleanObject(value, schema);
    }

    default:
      return value === null ? undefined : value;
  }
};

/**
 * Determine if a value should be treated as "empty" for a given schema.
 * Empty values are candidates to be removed/omitted from payloads.
 */
export const isValueEmptyForSchema = (
  schema: JSONSchema7 | undefined,
  value: unknown
): boolean => cleanValueForSchema(schema, value) === undefined;
